import { AppModal } from "@/components/AppModal";
import { useAuth } from "@/context/AuthContext";
import type { GameBoardOverlayInterface } from "@/game/context/GameBoardOverlayContext";
import { GameBoardOverlayContext } from "@/game/context/GameBoardOverlayContext";
import { useCanOfferDouble } from "@/game/hooks/useCanOfferDouble";
import { useGameLoader } from "@/game/hooks/useGameLoader";
import { useGamePlayState } from "@/game/hooks/useGamePlayState";
import { GamePlayAnimationType } from "@/game/utils/animationTypes.ts";
import { GameLogger } from "@/game/utils/gameLogger.ts";
import { useGameApi } from "@/hooks/useGameApi";
import { playSound } from "@/lib/audio";
import type { AtomicMove, PlayerColor } from "@nextgammon/shared";
import { Constants, Rules } from "@nextgammon/shared";
import { Assets } from "pixi.js";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ConnectionStatus } from "./ConnectionStatus";
import { EmoteDisplay } from "./EmoteDisplay";
import { EmoteSelector } from "./EmoteSelector";
import { GameBoard } from "./GameBoard";
import { GameBoardOverlay } from "./GameBoardOverlay";
import { GameOptionsMenu } from "./GameOptionsMenu";
import { PlayerInfo } from "./PlayerInfo";
import { TurnTimer } from "./TurnTimer";

// Map for storing one resolver per animation type.
type AnimationResolverMap = Partial<Record<GamePlayAnimationType, () => void>>;

export function Game() {
  const navigate = useNavigate();
  const { gameId: gameIdFromUrl } = useParams<{ gameId: string }>();
  const { token, userId } = useAuth();
  const gameApi = useGameApi();

  const [animatingState, setAnimatingState] = useState<number>(0);
  // State for managing animation promise resolution
  const animationResolverMap = useRef<AnimationResolverMap>({});

  // Emote system state
  const [emotesMuted, setEmotesMuted] = useState(false);

  // Double offer modal state
  const [showDoubleOfferModal, setShowDoubleOfferModal] = useState(false);

  // Resign confirmation modal state
  const [showResignConfirmModal, setShowResignConfirmModal] = useState(false);
  const [isResigning, setIsResigning] = useState(false);

  // Callback for useGamePlayState to request an animation step
  const handleHookRequestsAnimationStep = useCallback(
    (type: GamePlayAnimationType): Promise<void> => {
      GameLogger.logInfo(
        "Game",
        `Requesting animation step of type ${GamePlayAnimationType[type]}`
      );

      const resolver = animationResolverMap.current[type];
      if (resolver) {
        GameLogger.logWarn(
          "Game",
          `Animation step of type ${GamePlayAnimationType[type]} already requested.`
        );
        delete animationResolverMap.current[type];
        resolver();
      }

      setAnimatingState((flags) => flags | type);

      return new Promise<void>((resolve) => {
        animationResolverMap.current[type] = resolve;
      });
    },
    []
  );

  // Callback for GameBoard to signal animation step completion
  const handleBoardAnimationComplete = useCallback((type: GamePlayAnimationType) => {
    GameLogger.logInfo("Game", `Completing animation step of type ${GamePlayAnimationType[type]}`);

    const resolver = animationResolverMap.current[type];
    if (resolver) {
      setAnimatingState((flags) => flags & ~type);
      delete animationResolverMap.current[type];
      resolver();
    }
  }, []);

  const {
    loadedGameId,
    initialGameState: loadedGameState,
    initialGameDetails: loadedGameDetails,
    isLoadingInitialData,
    errorLoadingInitialData,
    isLoadingGamePlayAssets,
    errorLoadingGamePlayAssets,
    loadedGamePlayAssetBundles,
    hasJoinedGame
  } = useGameLoader(gameIdFromUrl, userId);

  const {
    displayGameState,
    displayGameDetails,
    currentDice, // To display current dice
    whiteDice, // White player's dice
    blackDice, // Black player's dice
    attemptLocalMove,
    undoLocalMove,
    getCurrentTurnMoves,
    getCurrentDiceRolled,
    isPlayerTurn, // Available for UI logic
    actionError,
    localTurnHistory, // Added to use for UI logic (e.g., Undo button)
    gameOver,
    winnerInfo
  } = useGamePlayState({
    initialGameStateFromLoader: loadedGameState,
    initialGameDetailsFromLoader: loadedGameDetails,
    currentUserId: userId,
    loadedGameId,
    onAnimateStep: handleHookRequestsAnimationStep, // Pass the animation callback
    hasJoinedGame
  });

  const [isApiActionInProgress, setIsApiActionInProgress] = useState(false); // For rollDice, submitTurn API calls
  const [gameRect, setGameRect] = useState<DOMRect | null>(null);
  const [gameHeaderRect, setGameHeaderRect] = useState<DOMRect | null>(null);
  // Used with the GameBoardOverlay API to define the play area bounds.
  const [gameContainer, setGameContainer] = useState<HTMLDivElement | null>(null);
  // Used with the GameBoardOverlay API to define the play area vertical offset.
  const [gameHeader, setGameHeader] = useState<HTMLDivElement | null>(null);
  const [gameBoardOverlayApi, setgameBoardOverlayApi] = useState<GameBoardOverlayInterface | null>(
    null
  );

  const gameContainerMount = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      setGameContainer(node);
    }
  }, []);
  const gameHeaderMount = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      setGameHeader(node);
    }
  }, []);

  // To inform UI about animation events in progress
  const isAnimating = animatingState !== 0;
  // Check if game is finished
  const isGameFinished = displayGameDetails?.status === "FINISHED" || gameOver;

  // Play game over sounds and close modals
  useEffect(() => {
    if (gameOver && winnerInfo) {
      if (winnerInfo.id === userId) {
        playSound("game-won");
      } else {
        playSound("game-lost");
      }
      // Close resign modal if it's open (resignation succeeded via SSE)
      setShowResignConfirmModal(false);
      setIsResigning(false);
    }
  }, [gameOver, winnerInfo, userId]);

  // Track if we've played the game start sound
  const gameStartSoundPlayedRef = useRef(false);

  // Play game start sound when game starts
  useEffect(() => {
    if (
      displayGameState &&
      displayGameDetails &&
      displayGameDetails.status === "IN_PROGRESS" &&
      !gameStartSoundPlayedRef.current
    ) {
      // Game has started - play the sound once
      GameLogger.logInfo("Game", "Playing game-start sound");
      playSound("game-start");
      gameStartSoundPlayedRef.current = true;
    }
  }, [displayGameState, displayGameDetails]);

  // Resize handling for the GameBoardOverlay
  useEffect(() => {
    if (!gameContainer) return;

    const updateGameRect = () => {
      if (!gameContainer) {
        GameLogger.logInfo("Game", "Board container ref not set yet, skipping layout update.");
      } else {
        const gameRect = gameContainer.getBoundingClientRect();

        GameLogger.logInfo(
          "Game",
          `Board container (update) size: ${gameRect.width}, ${gameRect.height}`
        );

        setGameRect(gameRect);

        if (gameHeader) {
          setGameHeaderRect(gameHeader.getBoundingClientRect());
        }
      }
    };

    updateGameRect();

    const resizeObserver = new ResizeObserver(updateGameRect);
    resizeObserver.observe(gameContainer);

    window.addEventListener("resize", updateGameRect);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateGameRect);
    };
  }, [gameContainer, gameHeader]);

  const rollDice = useCallback(async () => {
    if (!loadedGameId || !token || !displayGameState) {
      GameLogger.logError(
        "Game",
        "Cannot roll dice: Game not loaded, no token, or no display state."
      );
      return;
    }
    if (isGameFinished) {
      GameLogger.logError("Game", "Cannot roll dice: Game is finished.");
      return;
    }
    try {
      setIsApiActionInProgress(true);
      if (gameApi) {
        await gameApi.rollDice(loadedGameId);
      }
      // Game state updates via SSE
    } catch (err) {
      GameLogger.logError("Game", "Error rolling dice:", err);
      // UI should show actionError from useGamePlayState if it sets it for API errors too
    } finally {
      setIsApiActionInProgress(false);
    }
  }, [loadedGameId, token, displayGameState, isGameFinished]);

  // Auto-roll dice in speedgammon mode to save time
  useEffect(() => {
    const isMyTurn = isPlayerTurn();
    const diceRolled = getCurrentDiceRolled();

    if (
      displayGameDetails?.gameMode === "speedgammon" &&
      isMyTurn &&
      !diceRolled &&
      !isAnimating &&
      !isApiActionInProgress &&
      !isGameFinished &&
      displayGameState &&
      !displayGameDetails?.pendingDoubleFrom
    ) {
      const autoRollTimer = setTimeout(() => {
        // Re-check conditions before executing in case state changed during delay
        const stillMyTurn = isPlayerTurn();
        const stillNoDice = !getCurrentDiceRolled();

        if (stillMyTurn && stillNoDice && !isAnimating && !isApiActionInProgress) {
          rollDice();
        }
      }, 150);

      return () => clearTimeout(autoRollTimer);
    }
  }, [
    displayGameDetails?.gameMode,
    displayGameDetails?.pendingDoubleFrom,
    isAnimating,
    isApiActionInProgress,
    isGameFinished,
    displayGameState,
    rollDice,
    isPlayerTurn,
    getCurrentDiceRolled
  ]);

  const submitTurn = useCallback(async () => {
    // Only check for loadedGameId and token
    // Allow empty moves when a player has checkers on the bar but no valid moves
    if (!loadedGameId || !token) {
      GameLogger.logError("Game", "Cannot submit turn: Game not loaded or no token.");
      return;
    }
    if (isGameFinished) {
      GameLogger.logError("Game", "Cannot submit turn: Game is finished.");
      return;
    }

    // Get moves (might be empty array if no valid moves were possible)
    const movesToSubmit = getCurrentTurnMoves();

    try {
      setIsApiActionInProgress(true);
      // Always submit the moves array (empty or not)
      if (gameApi) {
        await gameApi.submitTurn(loadedGameId, movesToSubmit);
      }
      // Game state updates via SSE; localTurnHistory clears in hook
    } catch (err) {
      GameLogger.logError("Game", "Error submitting turn:", err);
    } finally {
      setIsApiActionInProgress(false);
    }
  }, [loadedGameId, token, getCurrentTurnMoves, isGameFinished]);

  const handleAttemptMove = useCallback(
    async (move: AtomicMove): Promise<boolean> => {
      if (isAnimating) {
        GameLogger.logInfo("Game", "Animation in progress, move attempt ignored.");
        return false;
      }
      GameLogger.logInfo("Game", "Attempting local move:", move);
      const result = await attemptLocalMove(move); // Hook handles errors via actionError
      return result.success;
    },
    [attemptLocalMove, isAnimating]
  );

  const handleUndoMove = useCallback(async () => {
    if (isAnimating) {
      GameLogger.logInfo("Game", "Animation in progress, undo attempt ignored.");
      return;
    }
    await undoLocalMove();
  }, [undoLocalMove, isAnimating]);

  const backButton = useCallback(
    (replaceHistory: boolean = false) => {
      // Unload gameplay assets.
      if (loadedGamePlayAssetBundles) {
        GameLogger.logDebug(
          "Game",
          "Unloading game play assets manifest:",
          loadedGamePlayAssetBundles
        );
        setTimeout(() => {
          Assets.unloadBundle(loadedGamePlayAssetBundles);
        }, 100); // Unload after a short delay to avoid renders with missing textures
      }

      navigate("/", { replace: replaceHistory });
    },
    [navigate, loadedGamePlayAssetBundles]
  );

  // Defensive check for missing timer data
  useEffect(() => {
    if (
      displayGameDetails?.status === "IN_PROGRESS" &&
      displayGameState &&
      displayGameDetails.currentTurn &&
      (!displayGameDetails.turnStartedAt || !displayGameDetails.turnDeadline)
    ) {
      GameLogger.logWarn("Game", "IN_PROGRESS game missing timer data, fetching game details", {
        currentTurn: displayGameDetails.currentTurn,
        turnStartedAt: displayGameDetails.turnStartedAt,
        turnDeadline: displayGameDetails.turnDeadline
      });

      // Fetch game details to get timing data
      if (gameApi && loadedGameId) {
        gameApi
          .getGame(loadedGameId)
          .then((response) => {
            GameLogger.logInfo("Game", "Fetched game details to get timer data:", response.game);
            // The SSE connection will handle the update
          })
          .catch((err: any) => {
            GameLogger.logError("Game", "Error fetching game details for timer:", err);
          });
      }
    }
  }, [displayGameDetails, displayGameState, token, loadedGameId]);

  const handleOfferDouble = useCallback(() => {
    // Show the modal for confirmation
    setShowDoubleOfferModal(true);
  }, []);

  const confirmDoubleOffer = useCallback(async () => {
    setShowDoubleOfferModal(false);

    if (!loadedGameId || !token) {
      GameLogger.logError("Game", "Cannot offer double: Game not loaded or no token.");
      return;
    }
    if (isGameFinished) {
      GameLogger.logError("Game", "Cannot offer double: Game is finished.");
      return;
    }
    try {
      setIsApiActionInProgress(true);
      if (gameApi) {
        await gameApi.offerDouble(loadedGameId);
      }
      // Game state updates via SSE
    } catch (err) {
      GameLogger.logError("Game", "Error offering double:", err);
    } finally {
      setIsApiActionInProgress(false);
    }
  }, [loadedGameId, token, isGameFinished]);

  const handleRespondToDouble = useCallback(
    async (accept: boolean) => {
      if (!loadedGameId || !token) {
        GameLogger.logError("Game", "Cannot respond to double: Game not loaded or no token.");
        return;
      }
      if (isGameFinished) {
        GameLogger.logError("Game", "Cannot respond to double: Game is finished.");
        return;
      }
      try {
        setIsApiActionInProgress(true);
        if (gameApi) {
          await gameApi.respondToDouble(loadedGameId, accept);
        }
        // Game state updates via SSE
      } catch (err) {
        GameLogger.logError("Game", "Error responding to double:", err);
      } finally {
        setIsApiActionInProgress(false);
      }
    },
    [loadedGameId, token, isGameFinished]
  );

  // Emote handlers
  const handleToggleMute = useCallback(() => {
    setEmotesMuted((prev) => !prev);
  }, []);

  const handleSendEmote = useCallback(
    async (emoteId: Constants.EmoteId) => {
      if (!loadedGameId || !token) {
        GameLogger.logError("Game", "Cannot send emote: Game not loaded or no token.");
        return;
      }
      try {
        if (gameApi) {
          await gameApi.sendEmote(loadedGameId, emoteId);
        }
      } catch (err) {
        GameLogger.logError("Game", "Error sending emote:", err);
      }
    },
    [loadedGameId, token]
  );

  // Resign handlers
  const handleResign = useCallback(() => {
    if (!isResigning) {
      setShowResignConfirmModal(true);
    }
  }, [isResigning]);

  const confirmResign = useCallback(async () => {
    if (!loadedGameId || !token) {
      GameLogger.logError("Game", "Cannot resign: Game not loaded or no token.");
      return;
    }
    if (isGameFinished) {
      GameLogger.logError("Game", "Cannot resign: Game is finished.");
      return;
    }

    setIsResigning(true);

    try {
      if (gameApi) {
        await gameApi.resign(loadedGameId);
      }
      // Game end handled via SSE
      setShowResignConfirmModal(false);
    } catch (err) {
      GameLogger.logError("Game", "Error resigning:", err);
      // Keep modal open on error so user knows something went wrong
      setIsResigning(false);
    }
  }, [loadedGameId, token, isGameFinished]);

  // Game creation now happens through matchmaking - no "new" route

  const playerColor: PlayerColor | null =
    userId === displayGameDetails.player1Id
      ? Constants.PLAYER_WHITE
      : userId === displayGameDetails.player2Id
        ? Constants.PLAYER_BLACK
        : null;

  const initialLoading = isLoadingInitialData || isLoadingGamePlayAssets;
  const overallLoading = initialLoading || isAnimating || isApiActionInProgress;

  const canOfferDouble = useCanOfferDouble({
    gameState: displayGameState,
    gameDetails: displayGameDetails,
    userId,
    diceRolled: getCurrentDiceRolled(),
    isAnimating: animatingState > 0,
    isLoading: overallLoading,
    isGameFinished
  });

  // For now, assume all players can afford doubles - in production this would
  // be calculated from actual user balance data when coin balances are implemented
  const opponentCanAffordDouble = true;
  const currentUserCanAffordDouble = true;

  // Calculate conditions for submit button and logging
  const diceRolled = getCurrentDiceRolled();
  const _areAnyDiceUnused =
    currentDice && currentDice.length > 0 && currentDice.some((d) => !d.used);
  // The button is disabled if localTurnHistory is present and empty (meaning dice rolled, but no moves made yet to enable submit)
  // Or if localTurnHistory is not even present (though it should be an empty array by default from the hook)
  // Let's stick to the exact logic previously in the button for this variable:
  const _isHistoryConditionMetForDisable = localTurnHistory && localTurnHistory.length === 0;

  // Check if any moves can be made with the currently unused dice
  let canMakeAnyMoveWithUnusedDice = false;
  let hasCheckersOnBar = false;
  let hasNoValidMovesFromBar = false;

  if (_areAnyDiceUnused && displayGameState && currentDice && diceRolled && Rules) {
    // Check if player has checkers on the bar
    hasCheckersOnBar =
      (displayGameState.currentPlayerColor === Constants.PLAYER_WHITE &&
        displayGameState.bar?.white > 0) ||
      (displayGameState.currentPlayerColor === Constants.PLAYER_BLACK &&
        displayGameState.bar?.black > 0);

    for (const die of currentDice) {
      if (!die.used) {
        // Ensure gameState has a valid board and currentPlayerId for Rules
        if (displayGameState.board && displayGameState.currentPlayerId) {
          const legalMovesForThisDie = Rules.findValidMovesForDie(displayGameState, die.value);
          if (legalMovesForThisDie && legalMovesForThisDie.length > 0) {
            canMakeAnyMoveWithUnusedDice = true;
            break; // Found a legal move, no need to check further
          }
        }
      }
    }

    // If player has checkers on the bar but can't make any moves
    hasNoValidMovesFromBar = hasCheckersOnBar && !canMakeAnyMoveWithUnusedDice;
  }

  // Memoize whether to show the game board to prevent unnecessary unmounts
  // MUST be before any early returns to avoid React hooks error
  const shouldShowGameBoard = useMemo(() => {
    return displayGameState && displayGameDetails && displayGameDetails.status !== "MATCHING";
  }, [displayGameState, displayGameDetails?.status]);

  // Log when game board visibility changes (but not in render)
  useEffect(() => {
    if (shouldShowGameBoard) {
      GameLogger.logInfo("Game", "Showing game board - status:", displayGameDetails?.status);
    }
  }, [shouldShowGameBoard, displayGameDetails?.status]);

  // Show loading spinner while:
  // 1. Loading initial data/assets OR
  // 2. Game is IN_PROGRESS/SETUP but we don't have game state yet (waiting for SSE)
  // 3. We have game details but status is still undefined (still loading)
  const waitingForGameState =
    displayGameDetails &&
    (displayGameDetails.status === "IN_PROGRESS" ||
      displayGameDetails.status === "SETUP" ||
      !displayGameDetails.status) &&
    !displayGameState;

  if (loadedGameId && (initialLoading || waitingForGameState) && !errorLoadingInitialData) {
    // Show loading if creating, or if loading initial data and no displayGameState yet
    return (
      <div className="flex flex-col items-center w-full min-h-screen bg-slate-900">
        <div className="w-full p-4 bg-slate-900 text-white flex items-center">
          <h1 className="text-xl font-bold">Setting Up Game</h1>
        </div>
        <div className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <svg
              className="animate-spin h-10 w-10 text-blue-500 mx-auto mb-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <p className="text-gray-300">Loading game...</p>
          </div>
        </div>
      </div>
    );
  }

  if (errorLoadingInitialData && loadedGameId) {
    return (
      <div className="flex flex-col items-center w-full min-h-screen bg-slate-900">
        <div className="w-full p-4 bg-slate-900 text-white flex items-center">
          <h1 className="text-xl font-bold">Game Error</h1>
        </div>
        <div className="flex-grow flex items-center justify-center p-4">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative max-w-md w-full">
            <strong className="font-bold">Error: </strong>
            <span className="block sm:inline">{errorLoadingInitialData}</span>
          </div>
        </div>
      </div>
    );
  }

  if (errorLoadingGamePlayAssets) {
    return (
      <div className="flex flex-col items-center w-full min-h-screen bg-slate-900">
        <div className="w-full p-4 bg-slate-900 text-white flex items-center">
          <h1 className="text-xl font-bold">Game Error</h1>
        </div>
        <div className="flex-grow flex items-center justify-center p-4">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative max-w-md w-full">
            <strong className="font-bold">Error: </strong>
            <span className="block sm:inline">Failed to load required assets</span>
          </div>
        </div>
      </div>
    );
  }

  // Show waiting screen for MATCHING status
  if (loadedGameId && displayGameDetails && displayGameDetails.status === "MATCHING") {
    return (
      <div className="flex flex-col items-center w-full min-h-screen bg-gradient-to-b from-blue-900 to-indigo-900 text-white">
        <div className="w-full p-4 bg-slate-900 text-white flex items-center shadow-md">
          <h1 className="text-xl font-bold">Finding Opponent</h1>
        </div>
        <div className="flex flex-col items-center justify-center flex-grow w-full max-w-md px-4">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-white border-t-transparent mb-8"></div>
          <h2 className="text-2xl font-bold mb-4">Looking for opponent...</h2>
          <p className="text-center text-blue-200 text-lg">
            Please wait while we find another player for you.
          </p>
          <p className="mt-4 text-center text-blue-300 text-sm">
            Game ID: {loadedGameId?.split("-")[0]}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-screen overflow-hidden bg-slate-900 flex flex-col">
      {actionError && (
        <div className="w-full px-4 py-1 bg-red-100 border-b border-red-400 text-red-700 flex-shrink-0">
          <p className="text-sm">Error: {actionError}</p>
        </div>
      )}

      {shouldShowGameBoard && (
        <div className="flex-1 flex overflow-hidden">
          <div className="flex w-full h-full">
            {/* Left Sidebar for Player Cards */}
            {/* Main Game Area */}
            <div
              className={`flex-1 shadow-xl overflow-hidden ${isAnimating ? "board-animating" : ""} ${overallLoading ? "board-loading" : ""} flex flex-col relative z-10`}
              ref={gameContainerMount}
            >
              {/* Overlay for animations, highlights, etc. */}
              <div className="flex-1 overflow-hidden">
                <GameBoardOverlayContext.Provider value={gameBoardOverlayApi}>
                  <GameBoardOverlay
                    gameArea={gameRect}
                    gameHeaderArea={gameHeaderRect} // No longer used but preserved in case a header element is re-added
                    setApiRef={setgameBoardOverlayApi} // Use state setter to update API
                  >
                    <GameBoard
                      gameState={displayGameState}
                      gameMode={displayGameDetails?.gameMode || "standard"}
                      gameDetails={displayGameDetails}
                      currentUserId={userId || ""}
                      isPlayerTurn={isPlayerTurn()}
                      currentDice={currentDice}
                      whiteDice={whiteDice}
                      blackDice={blackDice}
                      diceRolled={getCurrentDiceRolled()}
                      onAttemptMove={handleAttemptMove}
                      onOfferDouble={handleOfferDouble}
                      canOfferDouble={canOfferDouble}
                      animatingState={animatingState} // Pass animation status
                      onAnimationStepComplete={handleBoardAnimationComplete} // Pass animation completion callback
                      localTurnHistory={localTurnHistory}
                      gameOver={gameOver}
                      winnerInfo={winnerInfo}
                      // TODO: Consider adding canRoll, canSubmit states from useGamePlayState if logic becomes complex
                    />
                  </GameBoardOverlay>
                </GameBoardOverlayContext.Provider>
              </div>

              <div className="absolute inset-y-0 left-0 flex flex-col bg-transparent text-white p-2 shadow-xl w-40 overflow-y-hidden z-20 pointer-events-none">
                {/* Opponent on top */}
                <PlayerInfo
                  username={
                    userId === displayGameDetails.player1Id
                      ? displayGameDetails.player2Username || "Player 2"
                      : displayGameDetails.player1Username || "Player 1"
                  }
                  isCurrentTurn={
                    displayGameState?.currentPlayerId !== userId &&
                    displayGameState?.currentPlayerId !== null
                  }
                  playerColor={userId === displayGameDetails.player1Id ? "black" : "white"}
                  isCurrentUser={false}
                  userId={
                    userId === displayGameDetails.player1Id
                      ? displayGameDetails.player2Id || ""
                      : displayGameDetails.player1Id || ""
                  }
                  canAffordDouble={opponentCanAffordDouble}
                  gameState={displayGameState}
                ></PlayerInfo>

                {/* Current user on bottom */}
                <PlayerInfo
                  username={
                    userId === displayGameDetails.player1Id
                      ? displayGameDetails.player1Username || "Player 1"
                      : displayGameDetails.player2Username || "Player 2"
                  }
                  isCurrentTurn={displayGameState?.currentPlayerId === userId}
                  playerColor={userId === displayGameDetails.player1Id ? "white" : "black"}
                  isCurrentUser={true}
                  userId={userId || ""}
                  canAffordDouble={currentUserCanAffordDouble}
                  gameState={displayGameState}
                ></PlayerInfo>
              </div>

              {/* Turn Timer */}
              {displayGameDetails &&
                displayGameDetails.currentTurn &&
                (displayGameDetails.status === "IN_PROGRESS" ||
                  displayGameDetails.timeRemaining !== null ||
                  displayGameDetails.turnDeadline !== null) && (
                  <div className="absolute left-40">
                    <TurnTimer
                      turnDeadline={displayGameDetails.turnDeadline || null}
                      turnStartedAt={displayGameDetails.turnStartedAt || null}
                      currentTurn={displayGameDetails.currentTurn || null}
                      currentUserId={userId || ""}
                      serverTimeRemaining={displayGameDetails.timeRemaining}
                      gameMode={displayGameDetails.gameMode}
                      timeBank1={displayGameDetails.timeBank1}
                      timeBank2={displayGameDetails.timeBank2}
                      player1Id={displayGameDetails.player1Id}
                      player2Id={displayGameDetails.player2Id}
                    />
                  </div>
                )}

              {/* Emote display area */}
              <div className="absolute inset-y-0 left-40 flex flex-col bg-transparent text-white p-2 shadow-xl overflow-x-hidden overflow-y-hidden z-20 w-fit max-w-[calc(100vw-10rem)]">
                <div className="emote-display-area-top" data-user-id={userId || ""}>
                  <EmoteDisplay
                    playerId={userId || ""}
                    gameId={loadedGameId || ""}
                    isMuted={emotesMuted}
                  />
                </div>

                <div
                  className="emote-display-area-bottom"
                  data-user-id={
                    userId === displayGameDetails.player1Id
                      ? displayGameDetails.player2Id || ""
                      : displayGameDetails.player1Id || ""
                  }
                >
                  <EmoteDisplay
                    playerId={
                      userId === displayGameDetails.player1Id
                        ? displayGameDetails.player2Id || ""
                        : displayGameDetails.player1Id || ""
                    }
                    gameId={loadedGameId || ""}
                    isMuted={emotesMuted}
                  />
                </div>
              </div>

              {/* Action button overlay */}
              <div
                className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 ml-30 ${isPlayerTurn() ? "bg-slate-900/90" : "bg-slate-900/70"} text-white p-2 shadow-xl rounded-lg transition-colors`}
              >
                {displayGameState && isPlayerTurn() && !isGameFinished ? (
                  <div className="flex flex-col space-y-2">
                    {isPlayerTurn() &&
                      !diceRolled &&
                      !isAnimating &&
                      !displayGameDetails?.pendingDoubleFrom && (
                        <button
                          onClick={rollDice}
                          disabled={overallLoading || isGameFinished}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 text-sm font-semibold rounded shadow-md disabled:opacity-50 min-h-[44px]"
                        >
                          Roll Dice
                        </button>
                      )}
                    {!displayGameDetails?.pendingDoubleFrom && (
                      <button
                        onClick={submitTurn}
                        disabled={
                          overallLoading ||
                          isGameFinished ||
                          // Disable if:
                          // 1. Dice are unused AND legal moves can still be made with them
                          (_areAnyDiceUnused && canMakeAnyMoveWithUnusedDice) ||
                          // 2. No moves have been made yet in this turn (original condition)
                          // UNLESS the player can't make any moves (has checkers on the bar or can't bear off)
                          (_isHistoryConditionMetForDisable &&
                            (!diceRolled || canMakeAnyMoveWithUnusedDice))
                        }
                        className="px-3 py-2 bg-green-500 hover:bg-green-600 text-white text-sm font-semibold rounded shadow-md disabled:opacity-50 min-h-[44px]"
                      >
                        Submit Turn
                      </button>
                    )}
                    {localTurnHistory &&
                      localTurnHistory.length > 0 &&
                      !displayGameDetails?.pendingDoubleFrom && (
                        <button
                          onClick={handleUndoMove}
                          disabled={overallLoading || isGameFinished}
                          className="px-3 py-2 bg-yellow-500 hover:bg-yellow-600 text-white text-sm font-semibold rounded shadow-md disabled:opacity-50 min-h-[44px]"
                        >
                          Undo
                        </button>
                      )}
                  </div>
                ) : (
                  <div className="flex items-center justify-center px-2 py-2 min-h-[44px] text-blue-300">
                    <span className="text-sm">
                      {!displayGameState ? "Loading..." : "Waiting for turn..."}
                    </span>
                  </div>
                )}
              </div>

              {/* Connection status */}
              <div className="absolute right-1 top-1">
                <div className="text-xs">
                  <ConnectionStatus />
                </div>
              </div>

              {/* Game options controls */}
              {displayGameDetails.status === "IN_PROGRESS" && !isGameFinished && (
                <div className="absolute right-2 bottom-2">
                  <div className="flex gap-2 items-center">
                    <EmoteSelector onSend={handleSendEmote} disabled={false} />
                    <GameOptionsMenu
                      onResign={handleResign}
                      onToggleMute={handleToggleMute}
                      isMuted={emotesMuted}
                      disabled={false}
                    />
                  </div>
                </div>
              )}
            </div>{" "}
            {/* Close Main Game Area */}
          </div>{" "}
          {/* Close flex container */}
        </div>
      )}
      {!displayGameState &&
        !isLoadingInitialData &&
        !errorLoadingInitialData &&
        gameIdFromUrl !== "new" &&
        displayGameDetails &&
        displayGameDetails.player1Id && // Only show error if we actually have game details loaded
        displayGameDetails.status !== "MATCHING" &&
        displayGameDetails.status !== "IN_PROGRESS" && // Don't show error for IN_PROGRESS games waiting for SSE
        displayGameDetails.status !== "SETUP" && ( // Don't show error for SETUP games either
          <div className="flex-1 flex items-center justify-center">
            <p className="text-gray-500 text-xl">Game not found or unable to load.</p>
          </div>
        )}

      <AppModal
        show={
          !isAnimating &&
          (hasNoValidMovesFromBar ||
            (diceRolled && !canMakeAnyMoveWithUnusedDice && _areAnyDiceUnused))
        }
        variant="warning"
        title="No Moves Available"
        actions={[
          {
            label: "Pass Turn",
            onClick: submitTurn,
            disabled: overallLoading
          }
        ]}
      >
        <p>
          {hasNoValidMovesFromBar
            ? "You have checkers on the bar, but your current dice roll doesn't allow any moves."
            : "Your current dice roll doesn't allow any moves."}
        </p>
        <p className="mt-2">You must pass your turn.</p>
      </AppModal>

      <AppModal
        show={gameOver && !!winnerInfo}
        variant="success"
        title="Game Over!"
        actions={[
          {
            label: "Main Menu",
            onClick: () => backButton()
          }
        ]}
      >
        <p>
          {(() => {
            if (!winnerInfo) return "";
            const winnerUsername =
              winnerInfo.id === displayGameDetails.player1Id
                ? displayGameDetails.player1Username
                : displayGameDetails.player2Username;
            const opponentUsername =
              winnerInfo.id === displayGameDetails.player1Id
                ? displayGameDetails.player2Username
                : displayGameDetails.player1Username;

            // Handle resignation messages
            if (winnerInfo.endReason === "resignation") {
              if (winnerInfo.id === userId) {
                return `Victory! ${opponentUsername || "Your opponent"} has resigned.`;
              } else {
                return `You resigned. ${winnerUsername || "Your opponent"} wins.`;
              }
            }

            // Default win message
            return `${winnerUsername || `Player ${winnerInfo.color === Constants.PLAYER_WHITE ? "White" : "Black"}`} has won the game!`;
          })()}
        </p>
        {userId === winnerInfo?.id && winnerInfo.endReason !== "resignation" && (
          <p className="mt-3 font-bold">Congratulations on your victory!</p>
        )}
      </AppModal>

      {/* Double Offer Confirmation Modal (when player clicks cube) */}
      <AppModal
        show={showDoubleOfferModal}
        variant="info"
        title="Offer Double?"
        actions={[
          {
            label: "Cancel",
            onClick: () => setShowDoubleOfferModal(false),
            variant: "secondary"
          },
          {
            label: "Offer Double",
            onClick: confirmDoubleOffer,
            variant: "primary"
          }
        ]}
      >
        <p>
          This will double the stakes from {displayGameDetails?.cubeValue || 1} to{" "}
          {(displayGameDetails?.cubeValue || 1) * 2}.
        </p>
        <p className="mt-2">Your opponent can accept or decline (forfeiting the game).</p>
      </AppModal>

      {/* Double Offer Response Modal (when receiving a double) */}
      <AppModal
        show={
          !!displayGameDetails?.pendingDoubleFrom &&
          displayGameDetails.pendingDoubleFrom !== userId &&
          !isGameFinished
        }
        variant="info"
        title="Double Offered!"
        actions={[
          {
            label: "Decline",
            onClick: () => handleRespondToDouble(false),
            variant: "danger"
          },
          {
            label: "Accept",
            onClick: () => handleRespondToDouble(true),
            variant: "primary"
          }
        ]}
      >
        <p>
          Your opponent offers to double the stakes from {displayGameDetails?.cubeValue || 1} to{" "}
          {(displayGameDetails?.cubeValue || 1) * 2}.
        </p>
        <p className="mt-2">Accept to continue playing, or decline to forfeit the game.</p>
      </AppModal>

      {/* Waiting for Double Response (small indicator for offering player) */}
      {displayGameDetails?.pendingDoubleFrom === userId && !isGameFinished && (
        <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-blue-100 border border-blue-400 text-blue-700 rounded-lg px-4 py-2 z-40">
          <span className="text-sm font-medium">
            Waiting for opponent's response to double offer...
          </span>
        </div>
      )}

      {/* Resign Confirmation Modal */}
      <AppModal
        show={showResignConfirmModal}
        variant="error"
        title="Confirm Resign"
        actions={[
          {
            label: "Cancel",
            onClick: () => setShowResignConfirmModal(false),
            variant: "secondary",
            disabled: isResigning
          },
          {
            label: isResigning ? "Resigning..." : "Yes, Resign",
            onClick: confirmResign,
            variant: "danger",
            disabled: isResigning
          }
        ]}
      >
        {isResigning ? (
          <div className="text-center">
            <svg
              className="animate-spin h-8 w-8 text-red-500 mx-auto mb-3"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <p>Processing resignation...</p>
          </div>
        ) : null}
      </AppModal>
    </div>
  );
}
